/**
 * Monitor leghe (Admin · App, Andrea 2026-10-06): tutte le leghe esistenti,
 * chi c'e' dentro, se funziona tutto e cosa c'e' da sistemare. «Entra per
 * sistemare» apre la lega in supervisione: si lavora da presidente + admin
 * di quella lega senza esserne membri, finche' non si preme «Esci».
 */

import { useMemo, useState } from "react";
import { useAuth } from "../../hooks/useAuth";
import {
  type LegaMonitor,
  useDettaglioLega,
  useEntraInSupervisione,
  useMonitorLeghe,
} from "../../hooks/useMonitorLeghe";
import { etichetta } from "../common/etichette";
import { MobileShell } from "../shell/MobileShell";
import { localeCorrente } from "../../i18n";
import { tr } from "../../i18n";

const SALUTE: Record<LegaMonitor["salute"], { testo: string; cls: string; dot: string }> = {
  ok: { testo: tr("Tutto ok"), cls: "text-success", dot: "bg-success" },
  attenzione: { testo: tr("Da guardare"), cls: "text-warning", dot: "bg-warning" },
  problema: { testo: tr("Da sistemare"), cls: "text-danger", dot: "bg-danger" },
};

const LIVELLO: Record<string, string> = {
  problema: "text-danger",
  attenzione: "text-warning",
  info: "text-fg-muted",
};

/** Ricerca per nome, codice o chi ci gioca (Andrea 2026-10-08): senza accenti e maiuscole. */
function normalizza(s: string): string {
  return s
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

function corrisponde(l: LegaMonitor, q: string): boolean {
  if (!q) return true;
  const testo = normalizza(
    [l.nome, l.codice_invito ?? "", l.fondatore ?? "", ...l.amministratori, ...l.partecipanti].join(
      " ",
    ),
  );
  return q.split(/\s+/).every((parola) => testo.includes(parola));
}

function CodiceLega({ codice }: { codice: string | null }) {
  const [copiato, setCopiato] = useState(false);
  if (!codice) {
    return (
      <span className="text-fg-muted">
        {tr("Codice:")} <i>{"nessuno"}</i> {tr("(il presidente lo genera da «Le mie leghe»)")}
      </span>
    );
  }
  const copia = async () => {
    try {
      await navigator.clipboard.writeText(codice);
      setCopiato(true);
      setTimeout(() => setCopiato(false), 1500);
    } catch {
      /* niente appunti: resta visibile */
    }
  };
  return (
    <span className="inline-flex items-center gap-1.5">
      Codice:{" "}
      <code className="font-mono text-sm font-bold tracking-[0.15em] text-accent bg-primary-deep/60 border border-accent/30 rounded px-1.5 py-0.5 select-all">
        {codice}
      </code>
      <button
        type="button"
        onClick={copia}
        className="text-[10px] text-fg-muted hover:text-fg"
        title={tr("Copia il codice")}
      >
        {copiato ? tr("✓ copiato") : "copia"}
      </button>
    </span>
  );
}

function giorno(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString(localeCorrente(), { day: "numeric", month: "short" });
}

export function MonitorLeghePage() {
  const { user } = useAuth();
  const { data: leghe, isLoading, refetch, isFetching } = useMonitorLeghe();
  const [aperta, setAperta] = useState<string | null>(null);
  const [ricerca, setRicerca] = useState("");
  const entra = useEntraInSupervisione();
  const q = normalizza(ricerca);
  const visibili = useMemo(() => (leghe ?? []).filter((l) => corrisponde(l, q)), [leghe, q]);

  if (!user?.is_admin_app) {
    return (
      <MobileShell logoSuffix="LEGHE">
        <div className="card text-sm text-fg-muted">
          {tr("Il monitor delle leghe è riservato all'amministratore dell'app.")}
        </div>
      </MobileShell>
    );
  }

  const conteggi = { problema: 0, attenzione: 0, ok: 0 };
  for (const l of leghe ?? []) conteggi[l.salute] += 1;

  return (
    <MobileShell logoSuffix="LEGHE">
      <div className="card space-y-2">
        <div className="flex items-center justify-between gap-2">
          <div className="font-mono text-[10px] uppercase tracking-wider text-accent">
            {tr("Monitor leghe")}
          </div>
          <button
            type="button"
            className="text-[11px] text-accent hover:underline disabled:opacity-50"
            onClick={() => refetch()}
            disabled={isFetching}
          >
            {isFetching ? tr("Aggiorno…") : tr("↻ Aggiorna")}
          </button>
        </div>
        <div className="text-[11px] text-fg-muted">
          {tr(
            "Tutte le leghe dell'app. «Entra per sistemare» ti porta dentro la lega come presidente e admin, senza diventarne membro: un banner in alto ti ricorda dove sei e ti fa uscire.",
          )}
        </div>
        {leghe && (
          <div className="flex flex-wrap gap-3 text-xs">
            <span>
              <b className="text-fg">{leghe.length}</b> {"leghe"}
            </span>
            <span className="text-danger">{conteggi.problema} da sistemare</span>
            <span className="text-warning">{conteggi.attenzione} da guardare</span>
            <span className="text-success">{conteggi.ok} ok</span>
          </div>
        )}
        {/* Andrea 2026-10-08: con tante leghe serve cercarle */}
        <div className="relative">
          <input
            type="search"
            value={ricerca}
            onChange={(e) => setRicerca(e.target.value)}
            placeholder={tr("Cerca per nome, codice o partecipante…")}
            aria-label={tr("Cerca lega")}
            className="w-full rounded-lg bg-bg border border-primary-soft/50 pl-8 pr-3 py-2 text-sm text-fg placeholder:text-fg-muted focus:outline-none focus:border-accent"
          />
          <span aria-hidden className="absolute left-2.5 top-1/2 -translate-y-1/2 text-fg-muted">
            🔍
          </span>
        </div>
        {leghe && q && (
          <div className="text-[11px] text-fg-muted">
            {visibili.length === 0
              ? tr("Nessuna lega corrisponde.")
              : tr("{{v1}} su {{v2}} leghe", { v1: visibili.length, v2: leghe.length })}
          </div>
        )}
      </div>

      {isLoading && <div className="card text-sm text-fg-muted text-center">{tr("Carico…")}</div>}

      <div className="grid gap-3 md:grid-cols-2">
        {visibili.map((l) => {
          const s = SALUTE[l.salute];
          const qui = user.lega_corrente?.id === l.id;
          return (
            <div key={l.id} className="card space-y-2">
              <div className="flex items-start gap-2">
                <span className={`mt-1.5 w-2.5 h-2.5 rounded-full shrink-0 ${s.dot}`} />
                <div className="min-w-0 flex-1">
                  <div className="font-display text-lg text-fg leading-tight truncate">
                    {l.nome}
                    {/* Andrea 2026-10-08: "prima lega" = quella storica dell'app
                        (is_default); "partecipante" = ci giochi anche tu */}
                    {l.is_default && (
                      <span
                        className="ml-2 text-[9px] font-mono uppercase text-fg-muted"
                        title={tr("La prima lega dell'app, quella storica")}
                      >
                        {tr("prima lega")}
                      </span>
                    )}
                    {l.partecipo && (
                      <span
                        className="ml-2 text-[9px] font-mono uppercase text-accent"
                        title={tr("Sei membro di questa lega")}
                      >
                        {"partecipante"}
                      </span>
                    )}
                  </div>
                  <div className={`text-[11px] font-bold ${s.cls}`}>
                    {s.testo}
                    {l.stato !== "attiva" && (
                      <span className="text-fg-muted font-normal"> · {l.stato}</span>
                    )}
                  </div>
                </div>
                <button
                  type="button"
                  className="btn-primary text-xs px-3 py-1.5 shrink-0 disabled:opacity-50"
                  disabled={qui}
                  onClick={() => entra(l.id, "/gestione")}
                  title={qui ? tr("Sei già in questa lega") : tr("Entra nella lega per sistemarla")}
                >
                  {qui ? tr("Sei qui") : tr("Entra per sistemare")}
                </button>
              </div>

              <div className="grid grid-cols-3 gap-2 text-center">
                <Numero valore={l.n_membri} etichetta="membri" />
                <Numero
                  valore={`${l.n_squadre - l.n_squadre_libere}/${l.n_squadre}`}
                  etichetta="squadre assegnate"
                />
                <Numero
                  valore={l.giornate_totali ? `${l.giornate_calcolate}/${l.giornate_totali}` : "—"}
                  etichetta="giornate calcolate"
                />
              </div>

              <div className="text-[11px] text-fg-muted space-y-0.5">
                <div>
                  Stagione:{" "}
                  <b className="text-fg">
                    {l.stagione
                      ? tr("{{v1}} · {{v2}} · {{v3}}", {
                          v1: l.stagione.anno,
                          v2: etichetta(l.stagione.stato),
                          v3: etichetta(l.stagione.modalita),
                        })
                      : "nessuna"}
                  </b>
                  {l.prossima_da_giocare && <> · prossima G{l.prossima_da_giocare}</>}
                </div>
                <div>
                  {tr("Admin:")}{" "}
                  <span className="text-fg">{l.amministratori.join(", ") || "nessuno"}</span>
                  {l.fondatore && <> · fondata da {l.fondatore}</>}
                </div>
                <div>
                  Competizioni:{" "}
                  <span className="text-fg">{l.competizioni.join(", ") || "nessuna"}</span>
                </div>
                {/* Andrea 2026-10-08: codice sempre in vista, per far entrare o ritrovare */}
                <div className="pt-1">
                  <CodiceLega codice={l.codice_invito} />
                </div>
              </div>

              {l.segnalazioni.length > 0 && (
                <ul className="text-[11px] space-y-0.5 border-t border-primary/20 pt-2">
                  {l.segnalazioni.map((sg, i) => (
                    <li key={i} className={LIVELLO[sg.livello]}>
                      {sg.livello === "problema" ? "✗" : sg.livello === "attenzione" ? "!" : "·"}{" "}
                      {sg.testo}
                    </li>
                  ))}
                </ul>
              )}

              <button
                type="button"
                className="text-[11px] text-accent hover:underline"
                onClick={() => setAperta(aperta === l.id ? null : l.id)}
              >
                {aperta === l.id ? tr("▲ Nascondi chi c'è dentro") : tr("▼ Chi c'è dentro")}
              </button>
              {aperta === l.id && <Dettaglio legaId={l.id} />}
            </div>
          );
        })}
      </div>
    </MobileShell>
  );
}

function Numero({ valore, etichetta: et }: { valore: number | string; etichetta: string }) {
  return (
    <div className="bg-primary-deep/50 rounded-lg py-1.5">
      <div className="font-display text-base text-accent leading-tight">{valore}</div>
      <div className="text-[9px] uppercase tracking-wider text-fg-muted">{et}</div>
    </div>
  );
}

function Dettaglio({ legaId }: { legaId: string }) {
  const { data, isLoading } = useDettaglioLega(legaId);
  if (isLoading || !data) return <div className="text-[11px] text-fg-muted">{tr("Carico…")}</div>;
  return (
    <div className="space-y-3 text-[11px]">
      <div>
        <div className="font-mono uppercase tracking-wider text-[9px] text-accent mb-1">
          Membri ({data.membri.length})
        </div>
        <div className="divide-y divide-primary/10">
          {data.membri.map((m) => (
            <div key={m.user_id} className={`py-1 flex gap-2 ${m.attivo ? "" : "opacity-50"}`}>
              <div className="min-w-0 flex-1">
                <div className="text-fg truncate">
                  <b>{m.nome}</b>
                  {m.is_fondatore && <span className="text-accent"> {tr("· fondatore")}</span>}
                  {!m.attivo && <span> {tr("· uscito")}</span>}
                </div>
                <div className="text-fg-muted truncate">
                  {m.squadra ??
                    (m.secondo_di
                      ? tr("Secondo allenatore di {{v1}}", { v1: m.secondo_di })
                      : tr("senza squadra"))}{" "}
                  · {m.ruoli.map(etichetta).join(", ")}
                </div>
              </div>
              <div className="text-fg-muted shrink-0 text-right">
                <div className="truncate max-w-[9rem]">{m.email}</div>
                <div>ultimo accesso {giorno(m.ultimo_accesso)}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div>
        <div className="font-mono uppercase tracking-wider text-[9px] text-accent mb-1">
          Squadre ({data.squadre.length})
        </div>
        <div className="grid grid-cols-2 gap-x-3 gap-y-0.5">
          {data.squadre.map((s) => (
            <div key={s.id} className={`truncate ${s.uscita ? "line-through opacity-50" : ""}`}>
              <span className="text-fg">{s.nome}</span>
              <span className="text-fg-muted"> · {s.presidente ?? "libera"}</span>
            </div>
          ))}
        </div>
      </div>
      <div>
        <div className="font-mono uppercase tracking-wider text-[9px] text-accent mb-1">
          {tr("Stagioni e competizioni")}
        </div>
        {data.stagioni.length === 0 && (
          <div className="text-fg-muted">{tr("Nessuna stagione.")}</div>
        )}
        {data.stagioni.map((st) => (
          <div key={st.id}>
            <b className="text-fg">{st.anno}</b>{" "}
            <span className="text-fg-muted">
              {etichetta(st.stato)} · {etichetta(st.modalita)}
            </span>
            {data.competizioni_dettaglio
              .filter((c) => c.stagione === st.anno)
              .map((c) => (
                <div key={c.id} className="pl-3 text-fg-muted">
                  {c.nome} · {etichetta(c.stato)}
                </div>
              ))}
          </div>
        ))}
      </div>
    </div>
  );
}
